import { createSelector } from '@reduxjs/toolkit';
import { useContext } from 'react';

import { FlagsContext } from 'src/context';
import { useAppSelector } from 'src/hooks/useStore';
import { useGetNotificationsSettingsQuery } from 'src/services/api';
import { RootState } from 'src/store';

export const useMessagesNotificationCount = (): number => {
  const { EnableManyCompanies } = useContext(FlagsContext);

  const isClientUser = useAppSelector((state) => state.user.isClient);

  const selectTotalUnreadMessagesCount = createSelector(
    [
      (state: RootState) => state.messages.totalUnreadMessages,
      (state: RootState) => state.messages.totalUnreadMessagesByCompany,
      (state: RootState) => state.activeCompany.activeCompanyId,
      (_, manyCompaniesEnabled?: boolean) => manyCompaniesEnabled,
    ],
    (
      totalUnreadMessages,
      totalUnreadMessagesByCompany,
      activeCompanyId,
      manyCompaniesEnabled,
    ) => {
      if (!manyCompaniesEnabled || !isClientUser) {
        return totalUnreadMessages || 0;
      }

      if (totalUnreadMessagesByCompany && activeCompanyId) {
        return totalUnreadMessagesByCompany[activeCompanyId] || 0;
      }

      return 0;
    },
  );

  const totalUnreadMessagesCount = useAppSelector((state) =>
    selectTotalUnreadMessagesCount(state, EnableManyCompanies),
  );

  const { data: notificationSettings } = useGetNotificationsSettingsQuery(
    undefined,
    {
      skip: isClientUser,
    },
  );
  const disabledProductNotification = notificationSettings?.disableInProduct;
  const disableMessagesNotifications =
    notificationSettings?.notifyAbout.newMessages.disableInProduct;

  if (disabledProductNotification || disableMessagesNotifications) return 0;

  return totalUnreadMessagesCount;
};
